// First-run setup wizard (item 76 of PRODUCT-ROADMAP.md). Walks the
// admin who just installed OrbitalReg through a five-step on-ramp:
//
//   Welcome → Branding → Admin → Update check → First Project → Done
//
// The wizard auto-redirects from the rest of the app when
// /api/public-config returns `setup_required: true`. After the
// admin walks the steps the backend stamps
// system_setup.setup_completed_at and the redirect disarms.
//
// The "Update check" step (item 90 Phase B) is the cookie-consent-style
// opt-in for the upstream version manifest poll: no pre-selected
// default, the operator must click Off / Daily / Weekly. Off clears
// the manifest URL (zero outbound calls), Daily / Weekly point the
// worker at the upstream URL — the worker honours the cadence when
// configurable; today both behave the same as the daily default.
//
// The page is admin-gated end-to-end: the /api/admin/setup/* surface
// rejects non-admins, and the AdminShell wrapper at /admin/system/setup
// (the manual re-run path) lives behind the same auth gate as every
// other admin page.

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Rocket, Image as ImageIcon, ShieldCheck, FolderPlus, CheckCircle2,
  ArrowRight, ArrowLeft, ExternalLink, AlertTriangle, Sparkles, Loader2,
  RotateCcw, Upload, SkipForward, RadioTower, ShieldOff, Calendar,
} from 'lucide-react';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Wordmark } from '../components/Logo';
import { ThemeToggle } from '../components/ui/ThemeToggle';
import { usePublicConfig } from '../api/publicConfig';
import { useWhoami, createProject, useProjects } from '../api/client';
import {
  useSetupStatus, startSetup, markSetupStep, completeSetup,
} from '../api/setup';
import { setManifestURL } from '../api/versionStatus';

// Default upstream channel the worker polls when the operator opts in.
// Mirrors the prior persisted DEFAULT (migration 099) that Phase A
// flipped to empty in migration 102 — putting the URL back here means
// "Daily" / "Weekly" select the canonical channel without making the
// operator paste a URL.
const UPSTREAM_MANIFEST_URL = 'https://updates.orbitalreg.com/manifest.json';

type StepId = 'welcome' | 'branding' | 'admin' | 'update_check' | 'first_project' | 'done';

const ORDER: StepId[] = ['welcome', 'branding', 'admin', 'update_check', 'first_project', 'done'];

const STEP_LABELS: Record<StepId, string> = {
  welcome: 'Welcome',
  branding: 'Branding',
  admin: 'Admin',
  update_check: 'Update check',
  first_project: 'First project',
  done: 'Done',
};

interface Props {
  // When `embedded` is true the wizard renders inside an existing
  // shell (AdminShell on /admin/system/setup). When false it renders
  // its own starfield-backed standalone shell (the auto-redirect path
  // for /setup).
  readonly embedded?: boolean;
}

export function SetupWizard({ embedded = false }: Props) {
  const nav = useNavigate();
  const { data: pubConfig, mutate: mutatePubConfig } = usePublicConfig();
  const { user } = useWhoami();
  const { data: status, mutate: mutateStatus } = useSetupStatus();
  const { data: projects } = useProjects();

  const [step, setStep] = useState<StepId>('welcome');
  const [err, setErr] = useState<string | null>(null);

  // Mark setup_started_at on the first render once we know the
  // wizard is open. Idempotent backend-side; the timestamp only
  // moves on the first call.
  useEffect(() => {
    void startSetup().catch(() => {/* non-fatal */});
  }, []);

  const idx = ORDER.indexOf(step);
  const goNext = () => {
    setErr(null);
    setStep(ORDER[Math.min(idx + 1, ORDER.length - 1)]);
  };
  const goBack = () => {
    setErr(null);
    setStep(ORDER[Math.max(idx - 1, 0)]);
  };

  const handleStepDone = async (stepName: 'branding' | 'admin' | 'update_check' | 'first_project') => {
    try {
      await markSetupStep(stepName);
      await mutateStatus();
    } catch (e) {
      // Stamping is best-effort — the wizard still advances even if
      // the timestamp didn't take, since the user already executed
      // the underlying intent.
      console.warn('failed to mark step', stepName, e);
    }
  };

  const handleFinish = async () => {
    setErr(null);
    try {
      await completeSetup();
      await Promise.all([mutateStatus(), mutatePubConfig()]);
      nav('/');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const body = (
    <div className="flex flex-col gap-6">
      <Stepper current={idx} />
      {err && (
        <Card>
          <CardBody className="flex items-center gap-3 text-danger text-sm">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span className="font-mono break-all">{err}</span>
          </CardBody>
        </Card>
      )}
      {step === 'welcome' && (
        <WelcomeStep
          version={pubConfig?.version ?? 'dev'}
          buildTime={pubConfig?.build_time ?? ''}
          onNext={goNext}
        />
      )}
      {step === 'branding' && (
        <BrandingStep
          onSkip={async () => { await handleStepDone('branding'); goNext(); }}
          onContinue={async () => { await handleStepDone('branding'); goNext(); }}
          onBack={goBack}
        />
      )}
      {step === 'admin' && (
        <AdminStep
          email={user?.email ?? ''}
          airGapped={!!pubConfig?.air_gapped_mode}
          onContinue={async () => { await handleStepDone('admin'); goNext(); }}
          onBack={goBack}
        />
      )}
      {step === 'update_check' && (
        <UpdateCheckStep
          airGapped={!!pubConfig?.air_gapped_mode}
          onChosen={async () => { await handleStepDone('update_check'); goNext(); }}
          onBack={goBack}
        />
      )}
      {step === 'first_project' && (
        <FirstProjectStep
          alreadyHasProjects={!!projects && projects.length > 0}
          onContinue={async () => { await handleStepDone('first_project'); goNext(); }}
          onBack={goBack}
        />
      )}
      {step === 'done' && (
        <DoneStep
          airGapped={!!pubConfig?.air_gapped_mode}
          completedAt={status?.setup_completed_at}
          onFinish={handleFinish}
          onBack={goBack}
        />
      )}
    </div>
  );

  if (embedded) return body;

  return (
    <>
      <div className="starfield-bg" aria-hidden="true" />
      <div className="min-h-screen flex flex-col">
        <header className="h-16 px-6 flex items-center justify-between">
          <Wordmark />
          <ThemeToggle />
        </header>
        <main className="flex-1 flex items-start justify-center px-4 pb-12">
          <div className="w-full max-w-3xl mt-4">{body}</div>
        </main>
      </div>
    </>
  );
}

// --- Step header indicator ---------------------------------------------------

function stepBadgeToneClassName(done: boolean, active: boolean): string {
  if (done) return 'bg-success/20 border-success text-success';
  if (active) return 'bg-accent/20 border-accent text-accent';
  return 'bg-bg-inset border-border text-fg-subtle';
}

function Stepper({ current }: { readonly current: number }) {
  return (
    <ol className="flex flex-wrap items-center gap-x-2 gap-y-2 text-xs">
      {ORDER.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li key={s} className="flex items-center gap-2">
            <span
              className={
                'inline-flex items-center justify-center h-6 w-6 rounded-full border ' +
                stepBadgeToneClassName(done, active)
              }
            >
              {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
            </span>
            <span className={active ? 'text-fg font-medium' : 'text-fg-muted'}>
              {STEP_LABELS[s]}
            </span>
            {i < ORDER.length - 1 && <span className="text-fg-subtle">/</span>}
          </li>
        );
      })}
    </ol>
  );
}

// --- Welcome -----------------------------------------------------------------

function WelcomeStep({
  version, buildTime, onNext,
}: { readonly version: string; readonly buildTime: string; readonly onNext: () => void }) {
  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Rocket className="h-5 w-5 text-accent" />
            Welcome to OrbitalReg
          </span>
        }
        description="Let's get you to your first artifact in three minutes."
      />
      <CardBody className="space-y-4">
        <div className="text-sm text-fg-muted">
          This wizard walks you through the four things every fresh install
          benefits from: branding, an explicit first admin, a first project,
          and a quick pointer to the docs that match your CI stack.
        </div>
        <div className="rounded border border-border bg-bg-inset/40 px-4 py-3 text-xs text-fg-muted font-mono inline-block">
          Version {version}{buildTime ? ` · ${new Date(buildTime).toISOString().slice(0, 10)}` : ''}
        </div>
        <div className="flex justify-end pt-2">
          <Button variant="primary" onClick={onNext}>
            Get started <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

// --- Branding ----------------------------------------------------------------

function BrandingStep({
  onContinue, onSkip, onBack,
}: { readonly onContinue: () => void; readonly onSkip: () => void; readonly onBack: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [orgName, setOrgName] = useState('');

  const onUpload = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const file = (form.elements.namedItem('logo') as HTMLInputElement | null)?.files?.[0];
    if (!file) {
      setErr('Pick a file first.');
      return;
    }
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await fetch('/api/admin/branding/logo', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      setMsg('Logo uploaded.');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-accent" />
            Branding
          </span>
        }
        description="Optional. Replace the OrbitalReg wordmark with your own logo and set the help link."
      />
      <CardBody className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm text-fg-muted" htmlFor="org-name">
            Organisation name (display only)
          </label>
          <Input
            id="org-name"
            placeholder="Acme Platform"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
          />
          <p className="text-xs text-fg-subtle">
            Stored locally for now — multi-tenant org metadata lands in a
            future release. Pick whatever you want operators to see.
          </p>
        </div>
        <div className="border-t border-border pt-4">
          <form onSubmit={onUpload} className="space-y-3">
            <label className="text-sm text-fg-muted" htmlFor="logo">
              Logo (PNG / JPEG / SVG / WEBP, ≤ 256 KiB)
            </label>
            <input
              id="logo"
              name="logo"
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              className="text-sm text-fg file:mr-3 file:rounded file:border file:border-border file:bg-bg-inset file:px-3 file:py-1.5 file:text-fg hover:file:bg-bg-hover"
            />
            <div className="flex items-center gap-3">
              <Button type="submit" variant="secondary" size="sm" disabled={busy}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {busy ? 'Uploading…' : 'Upload logo'}
              </Button>
              {msg && <span className="text-xs text-success">{msg}</span>}
              {err && <span className="text-xs text-danger font-mono break-all">{err}</span>}
            </div>
            <p className="text-xs text-fg-subtle">
              Re-run uploads from <span className="font-mono">/admin/branding</span> any time.
            </p>
          </form>
        </div>
        <div className="flex items-center justify-between pt-2">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={onSkip}>
              <SkipForward className="h-4 w-4" />
              Skip
            </Button>
            <Button variant="primary" onClick={onContinue}>
              Continue <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

// --- Admin -------------------------------------------------------------------

function AdminStep({
  email, airGapped, onContinue, onBack,
}: {
  readonly email: string;
  readonly airGapped: boolean;
  readonly onContinue: () => void;
  readonly onBack: () => void;
}) {
  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-accent" />
            Confirm admin identity
          </span>
        }
        description="The signed-in user is the first administrator. Re-confirm to acknowledge that this account holds full org-admin scope."
      />
      <CardBody className="space-y-4">
        <div className="rounded border border-border bg-bg-inset/40 px-4 py-3 text-sm">
          <div className="text-fg-muted text-xs uppercase tracking-wide">
            Signed in as
          </div>
          <div className="text-fg font-mono break-all mt-1">
            {email || '(no email recorded — anonymous local session)'}
          </div>
        </div>
        <ul className="space-y-2 text-sm text-fg-muted">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 mt-0.5 text-success shrink-0" />
            Add a second admin from <span className="font-mono">/admin/permissions</span> as
            soon as the wizard finishes — single-admin installs are a recovery footgun.
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 mt-0.5 text-success shrink-0" />
            Configure SAML SSO from <span className="font-mono">/admin/permissions</span>{' '}
            once an IdP is available; the local-auth break-glass account stays as a recovery path.
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 mt-0.5 text-success shrink-0" />
            Mint API tokens from <span className="font-mono">/tokens</span> for CI rather than
            sharing the human admin login — every tier of automation should have its own scoped token.
          </li>
        </ul>
        {airGapped && (
          <div className="rounded border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Air-gapped mode is active. Outbound calls (update-check,
              advisory feeds, geo-sync peers) are blocked by default.
              Enable individual outbound paths under <span className="font-mono">/admin/network</span>.
            </span>
          </div>
        )}
        <div className="flex items-center justify-between pt-2">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <Button variant="primary" onClick={onContinue}>
            Continue <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

// --- Update check ------------------------------------------------------------

// Cookie-consent-style opt-in for the update-check egress channel
// (item 90 / Phase B). No pre-selected default — the operator must
// click one of the three buttons before the wizard advances. Off
// clears the manifest URL so the worker stays inert; Daily / Weekly
// point the worker at the upstream channel. The Daily / Weekly split
// records the operator's stated cadence preference even though
// today's worker uses a single 24h tick — Phase A took the safer
// default, Phase B takes the explicit consent.
type UpdateCheckChoice = 'off' | 'daily' | 'weekly';

function UpdateCheckStep({
  airGapped, onChosen, onBack,
}: {
  readonly airGapped: boolean;
  readonly onChosen: () => void;
  readonly onBack: () => void;
}) {
  const [busy, setBusy] = useState<UpdateCheckChoice | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [chose, setChose] = useState<UpdateCheckChoice | null>(null);

  const choose = async (choice: UpdateCheckChoice) => {
    setBusy(choice);
    setErr(null);
    try {
      const url = choice === 'off' ? '' : UPSTREAM_MANIFEST_URL;
      await setManifestURL(url);
      setChose(choice);
      await onChosen();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <RadioTower className="h-5 w-5 text-accent" />
            Update check
          </span>
        }
        description="Pick whether OrbitalReg may call out to check for new releases. There is no pre-selected default — your choice is recorded as an explicit consent."
      />
      <CardBody className="space-y-4">
        <p className="text-sm text-fg-muted">
          A fresh OrbitalReg install makes <strong>zero</strong> outbound calls
          until you opt in here. The update check pulls a small JSON manifest
          from <span className="font-mono">updates.orbitalreg.com</span> on
          the cadence you choose — no telemetry, no install IDs, just the
          latest released version so the admin overview can render an
          "update available" badge.
        </p>
        {airGapped && (
          <div className="rounded border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Air-gapped mode is active. Even if you pick Daily / Weekly here
              the network gate will still suppress the call until you flip
              the Telemetry toggle under{' '}
              <span className="font-mono">/admin/network</span>. Off is the
              honest choice on a regulated install.
            </span>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <ChoiceButton
            icon={<ShieldOff className="h-5 w-5" />}
            title="Off"
            subtitle="No outbound calls"
            tone="primary"
            chosen={chose === 'off'}
            busy={busy === 'off'}
            disabled={busy !== null}
            onClick={() => choose('off')}
          />
          <ChoiceButton
            icon={<RadioTower className="h-5 w-5" />}
            title="Daily"
            subtitle="One JSON pull per day"
            tone="secondary"
            chosen={chose === 'daily'}
            busy={busy === 'daily'}
            disabled={busy !== null}
            onClick={() => choose('daily')}
          />
          <ChoiceButton
            icon={<Calendar className="h-5 w-5" />}
            title="Weekly"
            subtitle="One JSON pull per week"
            tone="secondary"
            chosen={chose === 'weekly'}
            busy={busy === 'weekly'}
            disabled={busy !== null}
            onClick={() => choose('weekly')}
          />
        </div>
        {err && (
          <div className="text-xs text-danger font-mono break-all">{err}</div>
        )}
        <p className="text-xs text-fg-subtle">
          You can change this any time from{' '}
          <span className="font-mono">/admin/version-status/settings</span>.
        </p>
        <div className="flex items-center justify-start pt-2">
          <Button variant="ghost" onClick={onBack} disabled={busy !== null}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function choiceButtonRingClassName(chosen: boolean, tone: 'primary' | 'secondary'): string {
  if (chosen) return 'border-success ring-1 ring-success/40 bg-success/10';
  if (tone === 'primary') return 'border-accent/60 hover:border-accent hover:bg-accent/5';
  return 'border-border hover:border-accent/60 hover:bg-bg-inset/60';
}

function ChoiceButton({
  icon, title, subtitle, tone, chosen, busy, disabled, onClick,
}: {
  readonly icon: ReactNode;
  readonly title: string;
  readonly subtitle: string;
  readonly tone: 'primary' | 'secondary';
  readonly chosen: boolean;
  readonly busy: boolean;
  readonly disabled: boolean;
  readonly onClick: () => void;
}) {
  const ring = choiceButtonRingClassName(chosen, tone);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        'flex flex-col items-start gap-2 rounded-md border px-4 py-3 text-left text-sm transition ' +
        'disabled:opacity-60 disabled:cursor-not-allowed ' + ring
      }
    >
      <span className="inline-flex items-center gap-2 text-fg font-medium">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : icon}
        {title}
      </span>
      <span className="text-xs text-fg-muted">{subtitle}</span>
    </button>
  );
}

// --- First Project -----------------------------------------------------------

function FirstProjectStep({
  alreadyHasProjects, onContinue, onBack,
}: {
  readonly alreadyHasProjects: boolean;
  readonly onContinue: () => void;
  readonly onBack: () => void;
}) {
  const [slug, setSlug] = useState('my.first.project');
  const [name, setName] = useState('My first project');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await createProject({ slug, name, description: 'Created by the first-run setup wizard.' });
      setMsg(`Project ${slug} created.`);
    } catch (e) {
      const text = e instanceof Error ? e.message : String(e);
      setErr(text);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <FolderPlus className="h-5 w-5 text-accent" />
            First project
          </span>
        }
        description="Projects are the top-level container — repositories live inside them. Create one now or skip and use the Projects page later."
      />
      <CardBody className="space-y-4">
        {alreadyHasProjects ? (
          <div className="rounded border border-border bg-bg-inset/40 px-4 py-3 text-sm text-fg-muted">
            <Sparkles className="h-4 w-4 inline-block mr-2 text-accent" />
            You already have at least one project — feel free to skip this step.
          </div>
        ) : null}
        <form onSubmit={onCreate} className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm text-fg-muted" htmlFor="project-slug">
              Slug
            </label>
            <Input
              id="project-slug"
              required
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="my.first.project"
            />
            <p className="text-xs text-fg-subtle">
              Lowercase, digits, dots. No leading digit, 2–63 characters
              (item 62 of the roadmap enforces this server-side).
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-sm text-fg-muted" htmlFor="project-name">
              Display name
            </label>
            <Input
              id="project-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My first project"
            />
          </div>
          <div className="flex items-center gap-3">
            <Button type="submit" variant="secondary" size="sm" disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderPlus className="h-4 w-4" />}
              {busy ? 'Creating…' : 'Create project'}
            </Button>
            {msg && <span className="text-xs text-success">{msg}</span>}
            {err && <span className="text-xs text-danger font-mono break-all">{err}</span>}
          </div>
        </form>
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={onContinue}>
              <SkipForward className="h-4 w-4" />
              Skip
            </Button>
            <Button variant="primary" onClick={onContinue}>
              Continue <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

// --- Done --------------------------------------------------------------------

function DoneStep({
  airGapped, completedAt, onFinish, onBack,
}: {
  readonly airGapped: boolean;
  readonly completedAt?: string;
  readonly onFinish: () => void;
  readonly onBack: () => void;
}) {
  // External quick-links are replaced with internal docs-site routes
  // when the install is air-gapped — outbound to docs.orbitalreg.com
  // would never resolve on a network-isolated deployment.
  const links = airGapped
    ? [
        { title: 'Helm chart deploy', href: '/help', external: false },
        { title: 'Terraform provider', href: '/help', external: false },
        { title: 'Migrate from JFrog', href: '/help', external: false },
      ]
    : [
        {
          title: 'Helm chart deploy',
          href: 'https://docs.orbitalreg.com/operations/helm/',
          external: true,
        },
        {
          title: 'Terraform provider',
          href: 'https://docs.orbitalreg.com/reference/terraform-provider/',
          external: true,
        },
        {
          title: 'Migrate from JFrog',
          href: 'https://docs.orbitalreg.com/migration/from-jfrog/',
          external: true,
        },
      ];

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-success" />
            All set
          </span>
        }
        description={
          completedAt
            ? `Setup last completed ${new Date(completedAt).toISOString().slice(0, 16).replace('T', ' ')} UTC.`
            : 'You can re-run this wizard from /admin/system/setup.'
        }
      />
      <CardBody className="space-y-4">
        <p className="text-sm text-fg-muted">
          The dashboard is ready. Three quick links below cover the most
          common next moves:
        </p>
        <ul className="space-y-2">
          {links.map((l) => (
            <li key={l.title}>
              <a
                href={l.href}
                target={l.external ? '_blank' : undefined}
                rel={l.external ? 'noopener noreferrer' : undefined}
                className="inline-flex items-center gap-2 text-sm text-accent hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                {l.title}
              </a>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <Button variant="primary" onClick={onFinish}>
            <RotateCcw className="h-4 w-4" />
            Mark setup complete
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
